/**
 * Колбы (вариант B): три кольцевые стеклянные трубы.
 * Секции заполняются соком с анимацией уровня и волнистой поверхностью.
 */

import * as PIXI from 'pixi.js';
import { TextStyle } from 'pixi.js';
import {
  STATIC_RING_RADII,
  RING_WIDTH,
  RING_MULTIPLIERS,
  LAST_SEGMENT_BONUS,
  INDICATOR_ANGLE_RAD,
  type RingColor,
} from '@/config/drumConfig';
import { FRUIT_ASSETS } from '@/config/fruitAssets';
import {
  VIAL_GLASS_FILL,
  VIAL_GLASS_ALPHA,
  VIAL_GLASS_BORDER,
  VIAL_GLASS_BORDER_ALPHA,
  VIAL_GLASS_BORDER_WIDTH,
  VIAL_GLASS_HIGHLIGHT_WIDTH,
  VIAL_GLASS_HIGHLIGHT_COLOR,
  VIAL_GLASS_HIGHLIGHT_ALPHA,
  VIAL_GLASS_SEPARATOR_HIGHLIGHT_WIDTH,
  VIAL_SEPARATOR_COLOR,
  VIAL_SEPARATOR_ALPHA,
  VIAL_SEPARATOR_WIDTH,
  VIAL_JUICE_COLORS,
  VIAL_JUICE_ALPHA,
  VIAL_WAVE_AMPLITUDE,
  VIAL_WAVE_SPEED,
  VIAL_FILL_DURATION_MS,
  VIAL_DRAIN_DURATION_MS,
} from '@/config/vialConfig';
import type { FilledSectors } from '@/types/gameState';

/** Угол начала: сверху по центру, далее по часовой */
const START_ANGLE = INDICATOR_ANGLE_RAD;
/** Небольшое перекрытие по дуге (рад), чтобы между секциями не было артефактов/зазоров при растеризации */
const ANGULAR_OVERLAP = 0.004;

/** Скорость анимации заполнения/опорожнения (единицы прогресса за 1 мс) */
const FILL_DT_SCALE = 1 / VIAL_FILL_DURATION_MS;
const DRAIN_DT_SCALE = 1 / VIAL_DRAIN_DURATION_MS;
/** Макс. прирост времени за кадр для анимации (мс), чтобы не проскакивать за один кадр при большом delta */
const ANIM_MAX_DELTA_MS = 50;

const SPLASH_COUNT = 10;
const SPLASH_WALL_COUNT = 8;
const SPLASH_GRAVITY = 0.25;
const SPLASH_LIFETIME_MS = 600;
const SPLASH_RADIUS = 4;
const WALL_SPLASH_PROGRESS_START = 0.12;
const WALL_SPLASH_PROGRESS_END = 0.88;

/** Пузырьки в жидкости: меньше по количеству, появляются после заполнения секции (надуваются из 0) */
const BUBBLES_PER_SECTOR = 5;
const BUBBLE_RADIUS_MIN = 4;
const BUBBLE_RADIUS_MAX = 10;
const BUBBLE_FILL_ALPHA = 0.65;
const BUBBLE_HIGHLIGHT_ALPHA = 0.95;
const BUBBLE_EDGE_ALPHA = 0.5;
const BUBBLE_INFLATE_DURATION_MS = 450;

export class StaticRings extends PIXI.Container {
  private rings: Map<RingColor, PIXI.Graphics> = new Map();
  private ringHighlightStrips: Map<RingColor, PIXI.Graphics> = new Map();
  private ringBorders: Map<RingColor, PIXI.Graphics> = new Map();
  private labels: Map<RingColor, PIXI.Container> = new Map();
  private highlights: Map<RingColor, PIXI.Container> = new Map();
  private splashContainer = new PIXI.Container();
  private filledSectors: FilledSectors = { red: [], green: [], blue: [] };
  private animatingSegmentIndices: Record<RingColor, number[]> = { red: [], green: [], blue: [] };
  /** В каждый момент времени только один индекс на кольцо — текущий слив */
  private removingSegmentIndices: Record<RingColor, number[]> = { red: [], green: [], blue: [] };
  /** Очередь индексов на слив по кольцу: от последней к первой (от конца к началу) */
  private removalQueue: Record<RingColor, number[]> = { red: [], green: [], blue: [] };
  private highlightProgress = 0;
  private removeProgress: Record<RingColor, number> = { red: 0, green: 0, blue: 0 };
  /** Время завершения заполнения по (цвет, индекс секции) — для анимации «надувания» пузырьков */
  private sectorFilledAt: Record<RingColor, number[]> = { red: [], green: [], blue: [] };
  private wavePhase = 0;
  private splashParticles: Array<{ x: number; y: number; vx: number; vy: number; life: number; color: number }> = [];
  private onBonusInfoClick?: () => void;
  private onFillStart?: (color: RingColor, index: number) => void;
  private onDrainStart?: (color: RingColor, index: number) => void;

  constructor() {
    super();
    this.buildRings();
  }

  /** Вызвать при клике по иконке «i» у BONUS — показать описание бонусной игры */
  setBonusInfoCallback(callback: () => void): void {
    this.onBonusInfoClick = callback;
  }

  setOnFillStart(callback: (color: RingColor, index: number) => void): void {
    this.onFillStart = callback;
  }

  setOnDrainStart(callback: (color: RingColor, index: number) => void): void {
    this.onDrainStart = callback;
  }

  /** Есть ли незавершённая анимация заливки или слива секций (чтобы не переходить в IDLE и не применять новый спин до завершения). */
  isAnimating(): boolean {
    const hasFill = (this.animatingSegmentIndices.red?.length ?? 0) > 0 ||
      (this.animatingSegmentIndices.green?.length ?? 0) > 0 ||
      (this.animatingSegmentIndices.blue?.length ?? 0) > 0;
    const hasDrain = (this.removingSegmentIndices.red?.length ?? 0) > 0 ||
      (this.removingSegmentIndices.green?.length ?? 0) > 0 ||
      (this.removingSegmentIndices.blue?.length ?? 0) > 0;
    const hasQueue = (this.removalQueue.red?.length ?? 0) > 0 ||
      (this.removalQueue.green?.length ?? 0) > 0 ||
      (this.removalQueue.blue?.length ?? 0) > 0;
    return hasFill || hasDrain || hasQueue;
  }

  private createBonusInfoIcon(): PIXI.Container {
    const container = new PIXI.Container();
    const size = 20;
    const g = new PIXI.Graphics();
    g.beginFill(0xffffff, 0.9);
    g.lineStyle(1, 0x888888);
    g.drawCircle(0, 0, size / 2);
    g.endFill();
    container.addChild(g);
    const text = new PIXI.Text('i', new TextStyle({
      fontFamily: '"Jost"',
      fontSize: 14,
      fill: 0x333333,
      fontWeight: 'bold',
    }));
    text.anchor.set(0.5);
    container.addChild(text);
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointerdown', () => {
      this.onBonusInfoClick?.();
    });
    return container;
  }

  private createLayerBottomTexture(radius: number): PIXI.Texture {
    const size = radius * 2;
    const cx = radius;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;

    ctx.beginPath();
    ctx.arc(cx, cx, radius - 1, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();

    const angle = (318.21 * Math.PI) / 180;
    const x1 = cx + Math.cos(angle) * size;
    const y1 = cx + Math.sin(angle) * size;
    const grad = ctx.createLinearGradient(cx, cx, x1, y1);
    grad.addColorStop(0.1405, '#0d0f16');
    grad.addColorStop(0.8529, '#242730');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);

    const darkRadial = ctx.createRadialGradient(cx + 20, cx + 20, 0, cx + 20, cx + 20, radius);
    darkRadial.addColorStop(0, 'transparent');
    darkRadial.addColorStop(0.6, 'transparent');
    darkRadial.addColorStop(1, 'rgba(10, 12, 17, 0.4)');
    ctx.fillStyle = darkRadial;
    ctx.fillRect(0, 0, size, size);

    const lightRadial = ctx.createRadialGradient(cx - 20, cx - 20, 0, cx - 20, cx - 20, radius);
    lightRadial.addColorStop(0, 'rgba(35, 39, 50, 0.35)');
    lightRadial.addColorStop(0.5, 'transparent');
    lightRadial.addColorStop(1, 'transparent');
    ctx.fillStyle = lightRadial;
    ctx.fillRect(0, 0, size, size);

    return PIXI.Texture.from(canvas);
  }

  private buildRings(): void {
    const order: RingColor[] = ['red', 'green', 'blue'];
    for (const color of order) {
      const radius = STATIC_RING_RADII[color];
      const innerRadius = radius - RING_WIDTH;
      const multipliers = RING_MULTIPLIERS[color];
      if (!multipliers || multipliers.length === 0) {
        continue;
      }
      const sectorCount = multipliers.length;
      const angleStep = (2 * Math.PI) / sectorCount;

      const layerBottomTexture = this.createLayerBottomTexture(radius);
      const layerBottomSprite = new PIXI.Sprite(layerBottomTexture);
      layerBottomSprite.anchor.set(0.5);
      layerBottomSprite.x = 0;
      layerBottomSprite.y = 0;
      this.addChild(layerBottomSprite);

      const g = new PIXI.Graphics();
      const outerRadius = radius;

      // 1) Колба: полупрозрачное стекло по секциям (блик — отдельным слоем поверх жидкости)
      for (let i = 0; i < sectorCount; i++) {
        const startAngle = START_ANGLE + i * angleStep;
        const endAngle = START_ANGLE + (i + 1) * angleStep;
        g.beginFill(VIAL_GLASS_FILL, VIAL_GLASS_ALPHA);
        g.moveTo(Math.cos(startAngle) * outerRadius, Math.sin(startAngle) * outerRadius);
        g.arc(0, 0, outerRadius, startAngle, endAngle, false);
        g.lineTo(Math.cos(endAngle) * innerRadius, Math.sin(endAngle) * innerRadius);
        g.arc(0, 0, innerRadius, endAngle, startAngle, true);
        g.closePath();
        g.endFill();
      }

      // 2) Перегородки между секциями
      for (let i = 0; i <= sectorCount; i++) {
        const angle = START_ANGLE + i * angleStep;
        g.lineStyle(VIAL_SEPARATOR_WIDTH, VIAL_SEPARATOR_COLOR, VIAL_SEPARATOR_ALPHA);
        g.moveTo(Math.cos(angle) * innerRadius, Math.sin(angle) * innerRadius);
        g.lineTo(Math.cos(angle) * outerRadius, Math.sin(angle) * outerRadius);
        g.lineStyle(0);
      }

      // 3) Внутренний круг (фон под колбами)
      g.beginFill(0x0a0612);
      g.drawCircle(0, 0, innerRadius);
      g.endFill();

      this.addChild(g);
      this.rings.set(color, g);

      const highlightContainer = new PIXI.Container();
      highlightContainer.visible = true;
      highlightContainer.alpha = 1;
      this.addChild(highlightContainer);
      this.highlights.set(color, highlightContainer);

      // Блик по внешнему краю поверх жидкости (отражение на стекле)
      const highlightOuter = outerRadius;
      const highlightInner = Math.max(innerRadius, outerRadius - VIAL_GLASS_HIGHLIGHT_WIDTH);
      const highlightStripG = new PIXI.Graphics();
      for (let i = 0; i < sectorCount; i++) {
        const startAngle = START_ANGLE + i * angleStep;
        const endAngle = START_ANGLE + (i + 1) * angleStep;
        highlightStripG.beginFill(VIAL_GLASS_HIGHLIGHT_COLOR, VIAL_GLASS_HIGHLIGHT_ALPHA);
        highlightStripG.moveTo(Math.cos(startAngle) * highlightOuter, Math.sin(startAngle) * highlightOuter);
        highlightStripG.arc(0, 0, highlightOuter, startAngle, endAngle, false);
        highlightStripG.lineTo(Math.cos(endAngle) * highlightInner, Math.sin(endAngle) * highlightInner);
        highlightStripG.arc(0, 0, highlightInner, endAngle, startAngle, true);
        highlightStripG.closePath();
        highlightStripG.endFill();
      }
      for (let i = 0; i <= sectorCount; i++) {
        const angle = START_ANGLE + i * angleStep;
        const x1 = Math.cos(angle) * innerRadius;
        const y1 = Math.sin(angle) * innerRadius;
        const x2 = Math.cos(angle) * outerRadius;
        const y2 = Math.sin(angle) * outerRadius;
        highlightStripG.lineStyle(VIAL_GLASS_SEPARATOR_HIGHLIGHT_WIDTH, VIAL_GLASS_HIGHLIGHT_COLOR, VIAL_GLASS_HIGHLIGHT_ALPHA);
        highlightStripG.moveTo(x1, y1);
        highlightStripG.lineTo(x2, y2);
      }
      highlightStripG.lineStyle(0);
      this.addChild(highlightStripG);
      this.ringHighlightStrips.set(color, highlightStripG);

      // Бордеры поверх жидкости и блика: внешний и внутренний круг — одинаковый светлый бордер
      const borderG = new PIXI.Graphics();
      borderG.lineStyle(VIAL_GLASS_BORDER_WIDTH, VIAL_GLASS_BORDER, VIAL_GLASS_BORDER_ALPHA);
      borderG.drawCircle(0, 0, outerRadius);
      borderG.drawCircle(0, 0, innerRadius);
      borderG.lineStyle(0);
      this.addChild(borderG);
      this.ringBorders.set(color, borderG);

      const labelContainer = new PIXI.Container();
      const midRadius = radius - RING_WIDTH / 2;
      const fontScale = color === 'red' ? 20 : color === 'green' ? 18 : 16;
      
      // КРИТИЧЕСКИ ВАЖНО: Сегменты должны идти по часовой стрелке от 12 часов в порядке возрастания
      // Сегмент с индексом i начинается с угла START_ANGLE + i * angleStep
      // Сегмент 0 (i=0) начинается с START_ANGLE = -90° (12 часов) и содержит multipliers[0] (минимальный)
      // Сегмент 1 (i=1) идет следующим по часовой и содержит multipliers[1] (следующий по возрастанию)
      // И так далее...
      for (let i = 0; i < sectorCount; i++) {
        // Берем множитель из отсортированного массива по индексу i
        const multiplier = multipliers[i];
        if (multiplier === undefined) {
          continue;
        }
        
        // Последняя секция окружности — особая подпись: +7X (blue), +20X (green), BONUS (red)
        const isLastSegment = i === sectorCount - 1;
        const lastBonus = LAST_SEGMENT_BONUS[color];
        const labelText = isLastSegment && lastBonus === 'BONUS'
          ? 'BONUS'
          : isLastSegment && typeof lastBonus === 'number'
            ? `+${lastBonus}X`
            : `${multiplier}X`;
        
        // Угол середины сегмента i (для размещения метки): START_ANGLE + (i + 0.5) * angleStep
        const midAngle = START_ANGLE + (i + 0.5) * angleStep;
        
        const x = Math.cos(midAngle) * midRadius;
        const y = Math.sin(midAngle) * midRadius;
        const label = new PIXI.Text(
          labelText,
          new TextStyle({
            fontFamily: '"Jost"',
            fontSize: fontScale,
            fill: 0xffffff,
            fontStyle: 'italic',
            fontWeight: 'bolder',
            stroke: 0x0a0a14,
            strokeThickness: 3,
            dropShadow: true,
            dropShadowColor: 0x000000,
            dropShadowBlur: 3,
            dropShadowDistance: 2,
            dropShadowAlpha: 0.8,
          })
        );
        label.anchor.set(0.5);
        label.x = x;
        label.y = y;
        // Поворачиваем текст так, чтобы он был читаемым вдоль окружности
        // midAngle - угол позиции на окружности (в радианах)
        // Для читаемости вдоль окружности: угол текста = midAngle + 90° (чтобы текст был параллелен касательной)
        // В PixiJS угол в градусах, поворот по часовой стрелке
        label.angle = (midAngle * 180 / Math.PI) + 90;
        labelContainer.addChild(label);

        // Иконка «i» справа от текста BONUS (по касательной к окружности, с отступом от буквы S)
        if (color === 'red' && isLastSegment && lastBonus === 'BONUS') {
          const infoIcon = this.createBonusInfoIcon();
          const iconOffset = 54;
          infoIcon.x = x - iconOffset * Math.sin(midAngle);
          infoIcon.y = y + iconOffset * Math.cos(midAngle);
          infoIcon.angle = (midAngle * 180 / Math.PI) + 90;
          labelContainer.addChild(infoIcon);
        }
      }
      this.labels.set(color, labelContainer);

      // Порядок слоёв: стекло (g) -> жидкость (highlightContainer) -> блик (highlightStripG) -> бордеры (borderG) -> метки (labelContainer)
      this.addChild(labelContainer);

      // Маленькая иконка фрукта на 12 часов (над окружностью)
      const iconTexture = PIXI.Texture.from(this.getRingFruitAsset(color));
      const iconSprite = new PIXI.Sprite(iconTexture);
      iconSprite.anchor.set(0.5);
      const iconRadius = radius - RING_WIDTH * 0.55;
      iconSprite.x = Math.cos(START_ANGLE) * iconRadius;
      iconSprite.y = Math.sin(START_ANGLE) * iconRadius;
      const targetSize = 80;
      if (iconTexture.valid && iconTexture.width > 0) {
        const scale = targetSize / Math.max(iconTexture.width, iconTexture.height, 1);
        iconSprite.scale.set(scale);
      } else {
        const scale = targetSize / 256;
        iconSprite.scale.set(scale);
        iconTexture.on('update', () => {
          if (iconTexture.width > 0) {
            const nextScale = targetSize / Math.max(iconTexture.width, iconTexture.height, 1);
            iconSprite.scale.set(nextScale);
          }
        });
      }
      this.addChild(iconSprite);
    }
    this.addChild(this.splashContainer);
  }

  private getRingFruitAsset(color: RingColor): string {
    if (color === 'red') return FRUIT_ASSETS.symbol_watermelon;
    if (color === 'green') return FRUIT_ASSETS.symbol_dragon;
    return FRUIT_ASSETS.symbol_kiwi;
  }

  /** Обновить заполненные секторы; новые — анимация по часовой, снятые — слив по одной с конца к началу */
  setFilledSectors(filled: FilledSectors): void {
    const nextFilled: FilledSectors = { red: [], green: [], blue: [] };
    for (const color of ['red', 'green', 'blue'] as RingColor[]) {
      const multipliers = RING_MULTIPLIERS[color];
      const prev = this.filledSectors[color];
      const next = filled[color];
      const newlyAdded = next.filter((v) => !prev.includes(v));
      const newlyRemoved = prev.filter((v) => !next.includes(v));
      
      if (newlyAdded.length > 0) {
        const newIndices: number[] = [];
        for (const multiplier of newlyAdded) {
          const index = multipliers.indexOf(multiplier);
          if (index >= 0) {
            newIndices.push(index);
          }
        }
        if (newIndices.length > 0) {
          this.animatingSegmentIndices[color] = [...this.animatingSegmentIndices[color], ...newIndices];
          this.highlightProgress = 0;
          for (const segmentIndex of newIndices) {
            this.spawnSplash(color, segmentIndex);
            this.onFillStart?.(color, segmentIndex);
          }
        }
      }
      
      if (newlyRemoved.length > 0) {
        const removedIndices: number[] = [];
        for (const multiplier of newlyRemoved) {
          const index = multipliers.indexOf(multiplier);
          if (index >= 0) {
            removedIndices.push(index);
          }
        }
        removedIndices.sort((a, b) => b - a);
        this.removalQueue[color] = [...this.removalQueue[color], ...removedIndices];
        if (this.removingSegmentIndices[color].length === 0 && this.removalQueue[color].length > 0) {
          this.removingSegmentIndices[color] = [this.removalQueue[color].shift()!];
          this.removeProgress[color] = 0;
          this.onDrainStart?.(color, this.removingSegmentIndices[color][0]);
        }
        nextFilled[color] = prev;
      } else {
        nextFilled[color] = next;
      }
    }
    this.filledSectors = nextFilled;
    this.redrawLiquid();
  }

  private drawSplashes(): void {
    this.splashContainer.removeChildren();
    for (const p of this.splashParticles) {
      const alpha = Math.max(0, p.life / SPLASH_LIFETIME_MS);
      const g = new PIXI.Graphics();
      g.beginFill(p.color, alpha);
      g.drawCircle(p.x, p.y, SPLASH_RADIUS);
      g.endFill();
      this.splashContainer.addChild(g);
    }
  }

  private spawnSplash(color: RingColor, segmentIndex: number): void {
    const radius = STATIC_RING_RADII[color];
    const innerRadius = radius - RING_WIDTH;
    const sectorCount = RING_MULTIPLIERS[color].length;
    const angleStep = (2 * Math.PI) / sectorCount;
    const midAngle = START_ANGLE + (segmentIndex + 0.5) * angleStep;
    const midR = (innerRadius + radius) / 2;
    const ox = Math.cos(midAngle) * midR;
    const oy = Math.sin(midAngle) * midR;
    const fillColor = VIAL_JUICE_COLORS[color];
    for (let n = 0; n < SPLASH_COUNT; n++) {
      const angle = midAngle + (Math.random() - 0.5) * Math.PI * 0.6;
      const speed = 2 + Math.random() * 4;
      this.splashParticles.push({
        x: ox,
        y: oy,
        vx: Math.cos(angle) * speed,
        vy: -Math.abs(Math.sin(angle)) * speed - 1,
        life: SPLASH_LIFETIME_MS,
        color: fillColor,
      });
    }
  }

  /** Брызги при ударе жидкости о стенку секции (по окружности) */
  private spawnWallSplash(color: RingColor, segmentIndex: number, wall: 'start' | 'end'): void {
    const radius = STATIC_RING_RADII[color];
    const innerRadius = radius - RING_WIDTH;
    const sectorCount = RING_MULTIPLIERS[color].length;
    const angleStep = (2 * Math.PI) / sectorCount;
    const angle = wall === 'start'
      ? START_ANGLE + segmentIndex * angleStep
      : START_ANGLE + (segmentIndex + 1) * angleStep;
    const midR = (innerRadius + radius) / 2;
    const px = Math.cos(angle) * midR;
    const py = Math.sin(angle) * midR;
    const fillColor = VIAL_JUICE_COLORS[color];
    const normal = wall === 'start' ? 1 : -1;
    for (let n = 0; n < SPLASH_WALL_COUNT; n++) {
      const spread = (Math.random() - 0.5) * Math.PI * 0.5;
      const speed = 1.5 + Math.random() * 3;
      const vAngle = angle + normal * (Math.PI / 2 + spread);
      this.splashParticles.push({
        x: px,
        y: py,
        vx: Math.cos(vAngle) * speed,
        vy: Math.sin(vAngle) * speed,
        life: SPLASH_LIFETIME_MS,
        color: fillColor,
      });
    }
  }

  /** Вызывать каждый кадр: анимация заполнения/опорожнения, фаза волны */
  update(delta: number): void {
    const deltaMs = delta * (1000 / 60);
    this.wavePhase += deltaMs * VIAL_WAVE_SPEED;

    for (let i = this.splashParticles.length - 1; i >= 0; i--) {
      const p = this.splashParticles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vy += SPLASH_GRAVITY;
      p.life -= deltaMs;
      if (p.life <= 0) this.splashParticles.splice(i, 1);
    }
    this.drawSplashes();

    const hasAdd = this.animatingSegmentIndices.red.length > 0 ||
                   this.animatingSegmentIndices.green.length > 0 ||
                   this.animatingSegmentIndices.blue.length > 0;
    const hasRemove = this.removingSegmentIndices.red.length > 0 ||
                      this.removingSegmentIndices.green.length > 0 ||
                      this.removingSegmentIndices.blue.length > 0;

    if (hasAdd) {
      const prevProgress = this.highlightProgress;
      const animDelta = Math.min(deltaMs, ANIM_MAX_DELTA_MS);
      this.highlightProgress += animDelta * FILL_DT_SCALE;
      if (this.highlightProgress >= 1) {
        const now = performance.now();
        for (const c of ['red', 'green', 'blue'] as RingColor[]) {
          for (const idx of this.animatingSegmentIndices[c]) {
            if (!this.sectorFilledAt[c]) this.sectorFilledAt[c] = [];
            this.sectorFilledAt[c][idx] = now;
          }
        }
        this.highlightProgress = 1;
        this.animatingSegmentIndices = { red: [], green: [], blue: [] };
      }
      if (prevProgress < WALL_SPLASH_PROGRESS_START && this.highlightProgress >= WALL_SPLASH_PROGRESS_START) {
        for (const c of ['red', 'green', 'blue'] as RingColor[]) {
          for (const idx of this.animatingSegmentIndices[c]) {
            this.spawnWallSplash(c, idx, 'start');
          }
        }
      }
      if (prevProgress < WALL_SPLASH_PROGRESS_END && this.highlightProgress >= WALL_SPLASH_PROGRESS_END) {
        for (const c of ['red', 'green', 'blue'] as RingColor[]) {
          for (const idx of this.animatingSegmentIndices[c]) {
            this.spawnWallSplash(c, idx, 'end');
          }
        }
      }
      if (prevProgress < 1 && this.highlightProgress >= 1) {
        for (const c of ['red', 'green', 'blue'] as RingColor[]) {
          for (const idx of this.animatingSegmentIndices[c]) {
            this.spawnSplash(c, idx);
          }
        }
      }
      this.redrawLiquid();
    } else if (hasRemove) {
      const animDelta = Math.min(deltaMs, ANIM_MAX_DELTA_MS);
      for (const color of ['red', 'green', 'blue'] as RingColor[]) {
        if (this.removingSegmentIndices[color].length === 0) continue;
        this.removeProgress[color] += animDelta * DRAIN_DT_SCALE;
        if (this.removeProgress[color] >= 1) {
          const drainedIndex = this.removingSegmentIndices[color][0];
          this.removingSegmentIndices[color] = [];
          const mult = drainedIndex !== undefined ? RING_MULTIPLIERS[color]?.[drainedIndex] : undefined;
          if (mult !== undefined) {
            this.filledSectors[color] = this.filledSectors[color].filter((m) => m !== mult);
          }
          if (this.removalQueue[color].length > 0) {
            this.removingSegmentIndices[color] = [this.removalQueue[color].shift()!];
            this.removeProgress[color] = 0;
            this.onDrainStart?.(color, this.removingSegmentIndices[color][0]);
          } else {
            this.removeProgress[color] = 0;
          }
        }
      }
      this.redrawLiquid();
    } else {
      const hasFilled = this.filledSectors.red.length > 0 ||
                        this.filledSectors.green.length > 0 ||
                        this.filledSectors.blue.length > 0;
      if (hasFilled) {
        this.redrawLiquid();
      } else {
        const highlightContainers = this.highlights;
        if (Array.from(highlightContainers.values()).some(c => c.children.length > 0)) {
          highlightContainers.forEach(c => c.removeChildren());
        }
      }
    }
  }

  /**
   * Сектор как массив точек (обход по границе: внешняя дуга, внутренняя дуга).
   * Возвращает полигон сектора [startAngle..endAngle] с шагом по дуге.
   */
  private sectorPolygon(
    startAngle: number,
    endAngle: number,
    innerRadius: number,
    radius: number,
    arcSegments: number,
  ): { x: number; y: number }[] {
    const pts: { x: number; y: number }[] = [];
    const step = (endAngle - startAngle) / arcSegments;
    for (let k = 0; k <= arcSegments; k++) {
      const a = startAngle + k * step;
      pts.push({ x: radius * Math.cos(a), y: radius * Math.sin(a) });
    }
    for (let k = arcSegments; k >= 0; k--) {
      const a = startAngle + k * step;
      pts.push({ x: innerRadius * Math.cos(a), y: innerRadius * Math.sin(a) });
    }
    return pts;
  }

  /**
   * Отсечь полигон полуплоскостью y >= levelY (жидкость снизу, поверхность сверху; в экране y вниз).
   */
  private clipPolygonByHorizontal(
    pts: { x: number; y: number }[],
    levelY: number,
  ): { x: number; y: number }[] {
    const out: { x: number; y: number }[] = [];
    const inside = (p: { y: number }) => p.y >= levelY;
    const n = pts.length;
    for (let i = 0; i < n; i++) {
      const curr = pts[i];
      const prev = pts[(i - 1 + n) % n];
      const currIn = inside(curr);
      const prevIn = inside(prev);
      if (currIn) {
        if (!prevIn) {
          const t = (levelY - prev.y) / (curr.y - prev.y);
          if (t >= 0 && t <= 1) {
            out.push({ x: prev.x + t * (curr.x - prev.x), y: levelY });
          }
        }
        out.push(curr);
      } else if (prevIn) {
        const t = (levelY - prev.y) / (curr.y - prev.y);
        if (t >= 0 && t <= 1) {
          out.push({ x: prev.x + t * (curr.x - prev.x), y: levelY });
        }
      }
    }
    return out;
  }

  /**
   * Минимальная и максимальная y в секторе (для уровня воды).
   */
  private sectorYRange(
    startAngle: number,
    endAngle: number,
    innerRadius: number,
    radius: number,
  ): { yMin: number; yMax: number } {
    const y = (r: number, a: number) => r * Math.sin(a);
    const ys = [
      y(innerRadius, startAngle), y(radius, startAngle),
      y(innerRadius, endAngle), y(radius, endAngle),
    ];
    return { yMin: Math.min(...ys), yMax: Math.max(...ys) };
  }

  /** Рисует полигон жидкости в g (без маски). */
  private drawLiquidPolygon(
    g: PIXI.Graphics,
    polygon: { x: number; y: number }[],
    fillColor: number,
  ): void {
    g.beginFill(fillColor, VIAL_JUICE_ALPHA);
    g.lineStyle(0);
    g.moveTo(polygon[0].x, polygon[0].y);
    for (let i = 1; i < polygon.length; i++) g.lineTo(polygon[i].x, polygon[i].y);
    g.closePath();
    g.endFill();
  }

  /**
   * Жидкость льётся по окружности. sectorIndex для сдвига пузырьков и уникального seed.
   */
  private drawLiquidSweep(
    parent: PIXI.Container,
    startAngle: number,
    endAngle: number,
    innerRadius: number,
    radius: number,
    sweepProgress: number,
    fillColor: number,
    _sectorIndex: number,
  ): void {
    if (sweepProgress <= 0) return;
    const sectorSpan = endAngle - startAngle;
    const frontAngle = startAngle + sectorSpan * Math.min(1, sweepProgress);
    // Начинаем ровно от границы с предыдущей секцией (без отступа), чтобы не было визуального разрыва
    const wedgeStart = startAngle - ANGULAR_OVERLAP;
    const wedgeEnd = frontAngle + ANGULAR_OVERLAP;
    const { yMin } = this.sectorYRange(wedgeStart, wedgeEnd, innerRadius, radius);
    const waterLevel = yMin + 4 + VIAL_WAVE_AMPLITUDE * Math.sin(this.wavePhase);
    const pts = this.sectorPolygon(wedgeStart, wedgeEnd, innerRadius, radius, 20);
    const clipped = this.clipPolygonByHorizontal(pts, waterLevel);
    if (clipped.length < 3) return;
    const g = new PIXI.Graphics();
    this.drawLiquidPolygon(g, clipped, fillColor);
    parent.addChild(g);
  }

  /**
   * Пузырьки внутри области жидкости. Появляются после заполнения секции — плавно «надуваются» из 0 до полного размера (filledAt).
   */
  private seededRng(seed: number): () => number {
    let t = seed >>> 0;
    return () => {
      t += 0x6D2B79F5;
      let x = t;
      x = Math.imul(x ^ (x >>> 15), x | 1);
      x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
      return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
    };
  }

  private hashSeed(a: number, b: number, c: number): number {
    let x = (a ^ (b * 0x9e3779b9) ^ (c * 0x85ebca6b)) >>> 0;
    x ^= x >>> 16;
    x = Math.imul(x, 0x7feb352d);
    x ^= x >>> 15;
    x = Math.imul(x, 0x846ca68b);
    x ^= x >>> 16;
    return x >>> 0;
  }

  private drawBubblesInSector(
    g: PIXI.Graphics,
    startAngle: number,
    endAngle: number,
    innerRadius: number,
    radius: number,
    _fillColor: number,
    seed: number,
    minY?: number,
    sectorIndex?: number,
    filledAt?: number,
  ): void {
    const span = endAngle - startAngle;
    const rSpan = radius - innerRadius;
    const midR = (innerRadius + radius) / 2;
    const shiftRight = sectorIndex === 0 || sectorIndex === 1;
    const angleStart = shiftRight ? 0.45 : 0.22;
    const angleRange = shiftRight ? 0.42 : 0.52;
    const now = performance.now();
    const inflateT = filledAt && filledAt > 0
      ? Math.min(1, (now - filledAt) / BUBBLE_INFLATE_DURATION_MS)
      : 1;
    const easeOutBack = (t: number) => {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    };
    const scale = inflateT >= 1 ? 1 : easeOutBack(inflateT);
    const rng = this.seededRng(this.hashSeed(seed, sectorIndex ?? 0, Math.round(startAngle * 1e6)));
    const countJitter = Math.floor(rng() * 3) - 1;
    const bubbleCount = Math.max(3, BUBBLES_PER_SECTOR + countJitter);
    for (let i = 0; i < bubbleCount; i++) {
      const t = (i + rng()) / bubbleCount;
      const u = rng();
      const drift = (Math.sin(this.wavePhase * 0.9 + rng() * Math.PI * 2) * 0.5 + 0.5) * 0.08;
      const angleJitter = (rng() - 0.5) * 0.06;
      const angle = startAngle + span * (angleStart + angleRange * t + drift + angleJitter);
      const r = midR + rSpan * (0.18 + 0.45 * u + (rng() - 0.5) * 0.08);
      const bx = r * Math.cos(angle);
      const by = r * Math.sin(angle);
      if (minY != null && by < minY) continue;
      const bubbleR = (BUBBLE_RADIUS_MIN + (BUBBLE_RADIUS_MAX - BUBBLE_RADIUS_MIN) * rng()) * scale;
      if (bubbleR < 0.5) continue;
      const dist = Math.sqrt(bx * bx + by * by) || 1;
      const hShift = 0.35 + rng() * 0.25;
      const hx = bx - (bx / dist) * bubbleR * hShift;
      const hy = by - (by / dist) * bubbleR * hShift;
      g.lineStyle(1.5, 0xffffff, BUBBLE_EDGE_ALPHA);
      g.beginFill(0xffffff, BUBBLE_FILL_ALPHA);
      g.drawCircle(bx, by, bubbleR);
      g.endFill();
      g.lineStyle(0);
      g.beginFill(0xffffff, BUBBLE_HIGHLIGHT_ALPHA);
      g.drawCircle(hx, hy, bubbleR * (0.28 + rng() * 0.18));
      g.endFill();
    }
  }

  /**
   * Заполненная секция: жидкость и пузырьки в одном Graphics. Пузырьки только после заполнения, надуваются от 0 (filledAt).
   */
  private drawLiquidInSector(
    parent: PIXI.Container,
    startAngle: number,
    endAngle: number,
    innerRadius: number,
    radius: number,
    fillColor: number,
    withWave: boolean,
    sectorIndex: number,
    color: RingColor,
  ): void {
    const sectorStart = startAngle - ANGULAR_OVERLAP;
    const sectorEnd = endAngle + ANGULAR_OVERLAP;
    let polygon: { x: number; y: number }[];
    let waterLevel: number | undefined;
    if (withWave) {
      const { yMin } = this.sectorYRange(sectorStart, sectorEnd, innerRadius, radius);
      waterLevel = yMin + 6 + VIAL_WAVE_AMPLITUDE * Math.sin(this.wavePhase);
      const pts = this.sectorPolygon(sectorStart, sectorEnd, innerRadius, radius, 24);
      polygon = this.clipPolygonByHorizontal(pts, waterLevel);
    } else {
      polygon = this.sectorPolygon(sectorStart, sectorEnd, innerRadius, radius, 24);
    }
    if (polygon.length < 3) return;
    const container = new PIXI.Container();
    const liquid = new PIXI.Graphics();
    const bubbles = new PIXI.Graphics();
    const bubbleMask = new PIXI.Graphics();
    bubbleMask.beginFill(0xffffff, 1);
    bubbleMask.moveTo(polygon[0].x, polygon[0].y);
    for (let i = 1; i < polygon.length; i++) bubbleMask.lineTo(polygon[i].x, polygon[i].y);
    bubbleMask.closePath();
    bubbleMask.endFill();
    bubbleMask.renderable = false;
    bubbles.mask = bubbleMask;
    this.drawLiquidPolygon(liquid, polygon, fillColor);
    liquid.alpha = Math.min(VIAL_JUICE_ALPHA, 0.9);
    bubbles.alpha = 0.85;
    const bubbleSeed = ((Math.round(startAngle * 1e6) ^ fillColor) + sectorIndex * 7919 + Math.round(radius * 13)) % 0x7fffffff;
    const filledAt = this.sectorFilledAt[color]?.[sectorIndex];
    this.drawBubblesInSector(bubbles, sectorStart, sectorEnd, innerRadius, radius, fillColor, bubbleSeed, waterLevel, sectorIndex, filledAt);
    // Пузырьки под жидкостью и внутри маски секции
    container.addChild(bubbles);
    container.addChild(liquid);
    container.addChild(bubbleMask);
    parent.addChild(container);
  }

  private redrawLiquid(): void {
    for (const color of ['red', 'green', 'blue'] as RingColor[]) {
      const highlightContainer = this.highlights.get(color)!;
      if (!highlightContainer?.parent) continue;
      highlightContainer.visible = true;
      highlightContainer.alpha = 1;
      highlightContainer.removeChildren();

      const multipliers = RING_MULTIPLIERS[color];
      const filledValues = this.filledSectors[color];
      const animatingIndices = this.animatingSegmentIndices[color] ?? [];
      const removingIndices = this.removingSegmentIndices[color] ?? [];
      const sectorCount = multipliers.length;
      const angleStep = (2 * Math.PI) / sectorCount;
      const radius = STATIC_RING_RADII[color];
      const innerRadius = radius - RING_WIDTH;
      const fillColor = VIAL_JUICE_COLORS[color];

      let firstFilledIndex = -1;
      for (let idx = 0; idx < sectorCount; idx++) {
        const m = multipliers[idx];
        if (m !== undefined && filledValues.includes(m)) {
          firstFilledIndex = idx;
          break;
        }
      }

      for (let i = 0; i < sectorCount; i++) {
        const multiplier = multipliers[i];
        const isFilled = filledValues.includes(multiplier);
        const isAnimating = animatingIndices.includes(i);
        const isRemoving = removingIndices.includes(i);
        const startAngle = START_ANGLE + i * angleStep;
        const endAngle = START_ANGLE + (i + 1) * angleStep;

        if (isAnimating) {
          this.drawLiquidSweep(highlightContainer, startAngle, endAngle, innerRadius, radius, this.highlightProgress, fillColor, i);
        } else if (isRemoving) {
          const drainProgress = this.removeProgress[color] ?? 0;
          this.drawLiquidSweep(highlightContainer, startAngle, endAngle, innerRadius, radius, 1 - drainProgress, fillColor, i);
        } else if (isFilled) {
          const isFirstFilled = i === firstFilledIndex;
          this.drawLiquidInSector(highlightContainer, startAngle, endAngle, innerRadius, radius, fillColor, isFirstFilled, i, color);
        } else {
          continue;
        }
      }
    }
  }
}
